import React, { useState, useEffect } from 'react';
import { getUniqueValues, filterStudents } from '../utils/dataUtils';
import { StudentData } from '../utils/types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { loadSearchIndex, searchResumes } from '../utils/searchUtils';

interface SidebarProps {
  students: StudentData[];
  darkMode: boolean;
  filters: {
    preferredInstitutions: string[];
    majors: string[];
    minGpa: number;
    years: string[];
    skills: string[];
    keywordResults?: string[];
  };
  setFilters: (filters: {
    preferredInstitutions: string[];
    majors: string[];
    minGpa: number;
    years: string[];
    skills: string[];
    keywordResults?: string[];
  }) => void;
  toggleDarkMode: () => void;
  onLocationFilter: (zip: string, radius: number) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  students, 
  darkMode, 
  filters, 
  setFilters,
  toggleDarkMode,
  onLocationFilter 
}) => {
  const [savedFilterSets, setSavedFilterSets] = useState<{[key: string]: { preferredInstitutions: string[]; majors: string[]; minGpa: number; years: string[]; skills: string[] } }>({});
  const [filterName, setFilterName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [radius, setRadius] = useState(50);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    loadSearchIndex();
  }, []);

  const handleLocationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (zipCode) {
      onLocationFilter(zipCode, radius);
    }
  };

  const handleKeywordSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    
    const results = searchResumes(keywordSearch, students);
    setFilters({
      ...filters,
      keywordResults: results.map(s => s.Internal_ID)
    });
    
    setIsSearching(false);
  };

  const uniqueMajors = Array.from(new Set(students.map(s => s.major).filter(m => m && m.trim()))).sort();
  const uniqueYears = Array.from(new Set(students.map(s => s.year).filter(y => y && y.trim()))).sort();
  const allSkills = students.flatMap(s => s.skills);
  const uniqueSkills = Array.from(new Set(allSkills));

  // Debug logging for filter options
  console.log('Sidebar - Students count:', students.length);
  console.log('Sidebar - Unique majors:', uniqueMajors.length, uniqueMajors.slice(0, 3));
  console.log('Sidebar - Unique years:', uniqueYears.length, uniqueYears.slice(0, 3));

  const updateFilter = (key: keyof typeof filters, value: any) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };

  const saveFilterSet = () => {
    if (!filterName) return;
    setSavedFilterSets(prev => ({
      ...prev,
      [filterName]: { ...filters }
    }));
    setFilterName('');
  };

  const loadFilterSet = (name: string) => {
    const savedFilter = savedFilterSets[name];
    if (savedFilter) {
      setFilters(savedFilter);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      preferredInstitutions: [],
      majors: [],
      minGpa: 0,
      years: [],
      skills: [],
      keywordResults: undefined
    });
  };

  // Helper to generate CSV string
  const generateCSV = (students: StudentData[]) => {
    const header = ['University', 'Name', 'Academic email', 'Internal ID', 'University Phone'];
    const rows = students.map(s => [
      s.university,
      s.name,
      s.email,
      s.Internal_ID,
      s.phone
    ]);
    return [header, ...rows].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');
  };

  // Helper to generate filter criteria text
  const filterCriteriaText = (filter: any) => {
    return Object.entries(filter)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
      .join('\n');
  };

  // Download handler for a saved filter
  const handleDownloadFilter = async (filterName: string, filter: any) => {
    // Apply the filter to all students
    const filtered = filterStudents(students, filter);
    const zip = new JSZip();
    // Add filter criteria text
    zip.file('filter_criteria.txt', filterCriteriaText(filter));
    // Add CSV
    const csv = generateCSV(filtered);
    zip.file('students.csv', csv);
    // Add resumes
    await Promise.all(filtered.map(async (student: StudentData) => {
      if (student.Internal_ID) {
        try {
          const response = await fetch(`/byid/${student.Internal_ID}.pdf`);
          if (response.ok) {
            const blob = await response.blob();
            zip.file(`${student.Internal_ID}.pdf`, blob);
          }
        } catch (e) {
          // Ignore missing files
        }
      }
    }));
    // Generate and save zip
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${filterName.replace(/[^a-zA-Z0-9_-]/g, '_')}_filter.zip`);
  };

  return (
    <div className={`w-72 h-screen flex flex-col ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-black'} shadow-2xl rounded-xl overflow-hidden mr-6`}>
      <div className="flex items-center justify-between p-4 border-b">
        <span className="font-bold text-2xl">Filters</span>
        <button
          onClick={toggleDarkMode}
          className={`ml-2 p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
        >
          {darkMode ? '🌞' : '🌙'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <form onSubmit={handleLocationSubmit} className="mb-4">
          <label className="block text-sm font-semibold mb-1">Location</label>
          <input
            type="text"
            placeholder="Enter ZIP code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            className={`w-full p-2 rounded border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          />
          <div className="flex items-center space-x-2 mt-2">
            <input
              type="range"
              min="0"
              max="100"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="flex-1 accent-[#00A0DF]"
            />
            <span>{radius} miles</span>
          </div>
          <button
            type="submit"
            className="w-full p-2 rounded bg-[#00A0DF] text-white hover:bg-[#00A0DF]/90 mt-2"
          >
            Apply Location Filter
          </button>
        </form>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Preferred Institution</label>
          <select
            multiple
            value={filters.preferredInstitutions}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              updateFilter('preferredInstitutions', values);
            }}
            className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            {[
              "CORNELL UNIVERSITY",
              "FLORIDA INTERNATIONAL UNIVERSITY",
              "GEORGE MASON UNIVERSITY",
              "GEORGIA INSTITUTE OF TECHNOLOGY",
              "HOWARD UNIVERSITY",
              "KANSAS STATE UNIVERSITY",
              "LEHIGH UNIVERSITY",
              "LOUISIANA STATE UNIVERSITY & A&M COLG",
              "MICHIGAN TECHNOLOGICAL UNIV",
              "MILWAUKEE SCHOOL OF ENGINEERING",
              "MORGAN STATE UNIVERSITY",
              "NEW JERSEY INST OF TECHNOLOGY",
              "NEW YORK UNIVERSITY",
              "NORTH CAROLINA A&T STATE UNIV",
              "NORTHEASTERN UNIVERSITY",
              "PENNSYLVANIA STATE UNIVERSITY (THE)",
              "PRAIRIE VIEW A&M UNIVERSITY",
              "PURDUE UNIVERSITY",
              "RUTGERS, THE STATE UNIVERSITY OF NJ",
              "STEVENS INSTITUTE OF TECHNOLOGY",
              "TEXAS A&M UNIVERSITY",
              "TUSKEGEE UNIVERSITY",
              "UNIVERSITY OF ARKANSAS AT FAYETTEVILLE",
              "UNIVERSITY OF CALIFORNIA-DAVIS",
              "UNIVERSITY OF CALIFORNIA-SAN DIEGO",
              "UNIVERSITY OF CINCINNATI",
              "UNIVERSITY OF COLORADO-BOULDER",
              "UNIVERSITY OF DAYTON",
              "UNIVERSITY OF DELAWARE",
              "UNIVERSITY OF GEORGIA",
              "UNIVERSITY OF HOUSTON",
              "UNIVERSITY OF ILLINOIS URBANA-CHAMPAIGN",
              "UNIVERSITY OF MICHIGAN - ANN ARBOR",
              "UNIVERSITY OF NEBRASKA-LINCOLN",
              "UNIVERSITY OF SOUTHERN CALIFORNIA",
              "UNIVERSITY OF TEXAS-SAN ANTONIO",
              "UNIVERSITY OF WASHINGTON",
              "WAYNE STATE UNIVERSITY"
            ].map(inst => (
              <option key={inst} value={inst} title={inst}>{inst}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Major</label>
          <select
            multiple
            value={filters.majors}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              updateFilter('majors', values);
            }}
            className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            {uniqueMajors.map(major => (
              <option key={major} value={major} title={major}>{major}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Year</label>
          <select
            multiple
            value={filters.years.map(String)}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              updateFilter('years', values);
            }}
            className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            {uniqueYears.map(year => (
              <option key={year} value={year} title={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Minimum GPA</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="2.7"
              max="4.0"
              step="0.1"
              value={filters.minGpa}
              onChange={(e) => updateFilter('minGpa', parseFloat(e.target.value))}
              className="flex-1 accent-[#00A0DF]"
            />
            <span className="text-sm w-12 text-right">{filters.minGpa.toFixed(1)}</span>
          </div>
        </div>
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-2">Resume Keyword Search</h3>
          <form onSubmit={handleKeywordSearch} className="flex flex-col gap-2">
            <input
              type="text"
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              placeholder="Enter keywords (e.g., 'python AND machine learning')"
              className={`p-2 rounded border text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
            <button
              type="submit"
              disabled={isSearching}
              className="px-3 py-1.5 bg-[#00A0DF] text-white rounded hover:bg-[#00A0DF]/90 disabled:opacity-50 text-sm"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-1">
            Use AND, OR, NOT for boolean search
          </p>
        </div>
      </div>
      <div className="mt-4">
        <button
          onClick={clearAllFilters}
          className="w-full p-2 rounded bg-red-500 text-white hover:bg-red-600 mb-4"
        >
          Clear All Filters
        </button>
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="Filter set name"
          />
          <button
            onClick={saveFilterSet}
            className="shrink-0 p-2 rounded bg-[#00A0DF] text-white hover:bg-[#00A0DF]/90"
          >
            Save
          </button>
        </div>
        <div className="space-y-1">
          {Object.keys(savedFilterSets).map(name => (
            <div key={name} className="flex items-center space-x-2 mb-1">
              <span className="flex-1 truncate" title={name}>{name}</span>
              <button
                onClick={() => loadFilterSet(name)}
                className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-blue-200'} text-xs`}
              >
                Apply
              </button>
              <button
                onClick={() => handleDownloadFilter(name, savedFilterSets[name])}
                className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-green-200'} text-xs`}
              >
                Download
              </button>
              <button
                onClick={() => {
                  const updated = { ...savedFilterSets };
                  delete updated[name];
                  setSavedFilterSets(updated);
                }}
                className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-red-200'} text-xs`}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;